import React, { createContext, useContext, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { smartFeedAPI } from './smartFeedAPI';

// Subscription constants
export const SUBSCRIPTION_LEVELS = {
  FREE: 'FREE',
  STANDARD: 'STANDARD',
  PREMIUM: 'PREMIUM',
};

const SubscriptionContext = createContext({
  subscription: SUBSCRIPTION_LEVELS.FREE,
  isLoading: true,
  setSubscription: () => {},
});

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

export const SubscriptionProvider = ({ children }) => {
  const siteUser = useSelector((store) => store.user);
  const [subscription, setSubscription] = useState(SUBSCRIPTION_LEVELS.FREE);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        // First, try to get from user object
        const userSubscription = siteUser?.user?.subscription || 
                                siteUser?.user?.user_parameter?.subscription ||
                                siteUser?.user?.subscription_level;
        
        if (userSubscription) {
          // Normalize subscription value
          const normalized = userSubscription.toUpperCase();
          if (Object.values(SUBSCRIPTION_LEVELS).includes(normalized)) {
            setSubscription(normalized);
            setIsLoading(false);
            return;
          }
        }

        // If not in user object, try API
        const response = await smartFeedAPI.getUserSubscription();
        // Check multiple possible fields: subscription, subscription_level, plan
        const apiSubscription = response?.subscription || 
                                response?.subscription_level || 
                                response?.plan;
        
        if (apiSubscription) {
          // Normalize subscription value (handle lowercase "premium" -> "PREMIUM")
          const normalized = apiSubscription.toUpperCase();
          if (Object.values(SUBSCRIPTION_LEVELS).includes(normalized)) {
            setSubscription(normalized);
          } else {
            // If value doesn't match exactly, try to map common variations
            const subscriptionMap = {
              'PREMIUM': 'PREMIUM',
              'STANDARD': 'STANDARD',
              'FREE': 'FREE',
              'BASIC': 'FREE',
              'PRO': 'PREMIUM',
            };
            const mapped = subscriptionMap[normalized];
            if (mapped) {
              setSubscription(mapped);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
        // Default to FREE on error
        setSubscription(SUBSCRIPTION_LEVELS.FREE);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [siteUser]);

  const value = {
    subscription,
    isLoading,
    setSubscription,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;

